import { BarChart3, Gauge, Handshake, MapPin, MessageCircle, Route } from "lucide-react";
import { Button, RouteLink } from "../../components/ui";

const HOW_IT_WORKS_STEPS = [
  {
    step: 1,
    title: "Tell us about your business",
    description:
      "Answer structured questions about your company, stage, sector, and financial needs. Takes about 10 minutes via web or WhatsApp.",
  },
  {
    step: 2,
    title: "Get your Investment Readiness Score",
    description:
      "Fundy analyses your profile across key dimensions — team, market, traction, financials, and documentation — and gives you a clear score with prioritised actions to improve.",
  },
  {
    step: 3,
    title: "Get matched to funding types",
    description:
      "Based on your profile, Fundy recommends the right funding pathway: Grant, Seed Equity, Venture Capital, Debt, Revenue-based Finance, or Blended Finance.",
  },
  {
    step: 4,
    title: "Connect with funders",
    description:
      "Get connected to matched opportunities through Kenya Dealroom and the African Deal Stack — with guidance through every step of the actual raise process.",
  },
];

const FUNDING_PATHWAYS = [
  "Grant",
  "Seed Equity",
  "Venture Capital",
  "Debt",
  "Revenue-based Finance",
  "Blended Finance",
];

const FEATURE_CARDS = [
  {
    icon: Gauge,
    title: <>Investment Readiness<br />Scoring</>,
    description:
      "Understand where you stand before you pitch. Get scored on the factors investors actually evaluate, with actionable steps to improve your profile.",
  },
  {
    icon: BarChart3,
    title: "Powered by Dealroom",
    description:
      "Market intelligence from kenya.dealroom.co provides real funding data, valuations, and deal comparables specific to the Kenyan ecosystem.",
  },
  {
    icon: Handshake,
    title: "Deal Stack Referrals",
    description:
      "Connected to Africa's private market tech ecosystem. When you need due diligence support, deal documentation, or investor introductions, we route you to the right specialised platform.",
  },
  {
    icon: MapPin,
    title: <>Kenya-specific<br />intelligence</>,
    description:
      "Trained on local market dynamics, sector benchmarks, and ecosystem data. Understands fintech, mobility, health tech, and agriculture contexts.",
  },
  {
    icon: Route,
    title: "Six funding pathways",
    pathways: FUNDING_PATHWAYS,
  },
  {
    icon: MessageCircle,
    title: <>Works where<br />you work</>,
    description:
      "Complete your readiness assessment on the web or entirely over WhatsApp — no new app to learn, wherever you are in Kenya.",
  },
];


export const Home = () => {
  return (
    <article>
      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="relative min-h-[85vh] overflow-hidden bg-black">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('/image.png')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />
        <div className="relative z-10 flex min-h-[85vh] items-center px-6 pb-16 pt-32 sm:px-10 lg:px-20">
          <div className="max-w-[850px] space-y-6">
            <h1 className="animate-fade-up text-[32px] font-bold leading-[1.08] tracking-tight text-white sm:text-[40px] lg:text-[52px]">
              Find the right <br className="hidden sm:block" />
              funding for your startup
            </h1>
            <p className="animate-fade-up text-pretty text-[16px] font-normal leading-relaxed text-white/80 [animation-delay:100ms] sm:text-[18px]">
              An AI-powered platform that guides Kenyan entrepreneurs through the fundraising process from understanding
              your options to connecting with matched funders.
            </p>
            <div className="flex animate-fade-up flex-wrap gap-3 pt-2 [animation-delay:200ms]">
              <RouteLink to="/auth/signup">
                <Button
                  size="lg"
                  className="h-11 rounded-full bg-primary px-8 text-sm font-semibold text-white shadow-lg shadow-primary/40 transition-all duration-200 ease-out hover:bg-primary/90 hover:shadow-xl hover:shadow-primary/40"
                >
                  Get Started
                </Button>
              </RouteLink>
              <a
                href="https://wa.me/254793457085"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  size="lg"
                  variant="outline"
                  className="h-11 rounded-full border-white/30 bg-white/10 px-8 text-sm font-semibold text-white backdrop-blur-md transition-all duration-200 ease-out hover:border-white/60 hover:bg-white/20 hover:text-white"
                >
                  <MessageCircle className="mr-2 h-4 w-4" />
                  Chat on WhatsApp
                </Button>
              </a>
            </div>
            <p className="flex animate-fade-up flex-wrap items-center gap-x-2.5 gap-y-1 pt-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/50 [animation-delay:300ms]">
              Powered by
              <span className="text-white/90">Dealroom</span>
              <span aria-hidden className="h-1 w-1 rounded-full bg-primary" />
              <span className="text-white/90">African Deal Stack</span>
            </p>
          </div>
        </div>
      </section>

      {/* ── Problem ──────────────────────────────────────────── */}
      <section className="bg-white px-6 py-20 sm:px-10 lg:px-20">
        <div className="grid items-center gap-8 lg:grid-cols-[2fr_1fr] lg:gap-16">
          <div className="space-y-6">
            <h2 className="text-[28px] font-normal leading-[1.12] tracking-tight text-body-text sm:text-[32px] lg:text-[38px]">
              Fundraising shouldn&apos;t <br className="hidden sm:block" />
              depend on{" "}
              <span className="font-bold underline decoration-primary/30 decoration-[5px] underline-offset-8">
                who you know
              </span>
            </h2>
            <p className="max-w-2xl text-pretty text-[16px] font-normal leading-relaxed text-body-text/70 sm:text-[17px]">
              Kenya&apos;s startup funding ecosystem is fragmented. Founders outside Nairobi&apos;s tech hub face
              limited investor connections, overwhelming processes, and unclear paths to capital. Existing tools
              operate in silos. Fundy brings them together.
            </p>
            <p className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-body-text/40">
              Powered by
              <span className="text-body-text/80">Fundy AI</span>
            </p>
          </div>
          <div className="flex justify-center lg:justify-end">
            <div className="relative">
              <div aria-hidden className="absolute -inset-5 rounded-full bg-primary/10 blur-2xl" />
              <div className="relative h-64 w-64 overflow-hidden rounded-full shadow-xl ring-1 ring-black/10 sm:h-80 sm:w-80">
                <img
                  src="/image2.png"
                  alt="Kenyan founders collaborating"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── How It Works ─────────────────────────────────────── */}
      <section className="bg-light-gray px-6 py-20 sm:px-10 lg:px-20">
        <div className="mx-auto max-w-6xl">
          <div className="grid items-start gap-12 lg:grid-cols-2">
            {/* App screenshot */}
            <img
              src="/how-it-works.png"
              alt="How Fundy works"
              className="w-full rounded-2xl object-cover shadow-xl shadow-black/10 ring-1 ring-black/10 lg:sticky lg:top-28"
            />

            {/* Steps */}
            <div className="space-y-4">
              <div className="mb-6">
                <h2 className="text-[22px] font-bold tracking-tight text-body-text sm:text-[26px]">How it works</h2>
                <p className="mt-1.5 text-[15px] text-body-text/60">Your AI fundraising guide</p>
              </div>
              {HOW_IT_WORKS_STEPS.map((item) => (
                <div
                  key={item.step}
                  className="flex gap-4 rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-primary/25 hover:shadow-md"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[14px] font-semibold text-primary">
                    {item.step}
                  </span>
                  <div className="min-w-0">
                    <p className="pt-1 text-[16px] font-semibold leading-snug text-body-text">{item.title}</p>
                    {item.description && (
                      <p className="mt-1.5 text-[15px] font-normal leading-relaxed text-body-text/70">{item.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Built for Kenyan Founders + Partners (shared dark bg) ── */}
      <section className="relative overflow-hidden">
        {/* Background */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('/image.png')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628]/95 via-[#0a1628]/85 to-[#0a1628]/95" />

        <div className="relative z-10 px-6 py-20 sm:px-10 lg:px-20">
          <div className="space-y-14">
            {/* Feature cards block */}
            <div className="space-y-8">
              <h2 className="text-[28px] font-bold leading-[1.12] tracking-tight text-white sm:text-[32px] lg:text-[38px]">
                Built for <br className="hidden sm:block" />
                Kenyan founders
              </h2>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {FEATURE_CARDS.map((card, i) => (
                  <div
                    key={i}
                    className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-1 hover:shadow-lg"
                  >
                    <span className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <card.icon className="h-5 w-5" strokeWidth={1.75} />
                    </span>
                    <h3 className="mb-2 text-[18px] font-semibold tracking-tight text-body-text">{card.title}</h3>
                    {"pathways" in card && card.pathways ? (
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {card.pathways.map((pathway) => (
                          <span
                            key={pathway}
                            className="rounded-full bg-light-gray px-2.5 py-1 text-[13px] font-medium text-body-text/70"
                          >
                            {pathway}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[15px] font-normal leading-relaxed text-body-text/70">{card.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Partners block */}
            <div className="space-y-4 pt-8">
              <h3 className="text-[22px] font-bold tracking-tight text-white sm:text-[24px]">Part of the Frontier Tech Hub ecosystem</h3>
              <p className="max-w-4xl text-pretty text-[16px] font-normal leading-relaxed text-white/75 sm:text-[17px]">
                Developed under the UK Foreign, Commonwealth &amp; Development Office&apos;s Frontier Tech Hub
                programme. Fundy serves as a common front door to Africa&apos;s private market tech ecosystem, driving
                traffic to specialised platforms while democratising access to investment networks.
              </p>
              <p className="flex flex-wrap items-center gap-x-2.5 gap-y-1 pt-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/50">
                Partners
                <span className="text-[13px] normal-case tracking-normal text-white/90">Kenya Dealroom</span>
                <span aria-hidden className="h-1 w-1 rounded-full bg-primary" />
                <span className="text-[13px] normal-case tracking-normal text-white/90">African Deal Stack Consortium</span>
              </p>
            </div>
          </div>
        </div>
      </section>
    </article>
  );
};
